/**
 * Coverage gaps: regions from the scenario presets, plus the scripted Guam
 * demo (fly to the Indian Ocean gap, count the blackout minutes, add the Guam
 * station and re-run to show the gap closing).
 */
import { MapPin, Play, Plus, Square } from 'lucide-react';
import { Chip, StatusIcon, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { GUAM } from '../../constants';
import { getGlobeEngine } from '../../globe/engineHandle';
import { useLoader } from '../../store';
import type { GapRegion } from '../../types';
import { addGuamStation, guamStationAdded, startGuamDemo, stopGuamDemo, useGuamDemo } from '../guamDemo';

const SEVERITY_TONE = { critical: 'warning', high: 'warning', medium: 'caution', low: 'neutral' } as const;

export function GapsPanel() {
  const dataset = useLoader((s) => s.dataset);
  const guamActive = useLoader((s) => s.guamDemoActive);
  const blackout = useGuamDemo((s) => s.blackout);
  const hasGuam = guamStationAdded();
  const regions: GapRegion[] = dataset?.presets.find((p) => p.id === 'guam_moment')?.gapRegions ?? [];

  return (
    <div className="flex flex-col gap-3 p-panel text-data">
      <div className="flex flex-wrap items-center gap-2 rounded-panel border border-hairline bg-inset/50 p-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink">Coverage gap demo (G)</div>
          <div className="text-ink-2">
            Illustrative scenario: adding a Guam station reduces the example gap. The counter is scripted; use
            the analysis results for calculated coverage.
          </div>
        </div>
        <div
          className={cn(
            'flex items-center gap-1.5 font-mono text-2xl',
            blackout > GUAM.BLACKOUT_WITH_GUAM ? 'text-warning' : 'text-nominal',
          )}
          title="Illustrative blackout minutes per orbit, not a measured result"
        >
          <StatusIcon
            tone={blackout > GUAM.BLACKOUT_WITH_GUAM ? 'warning' : 'nominal'}
            label={blackout > GUAM.BLACKOUT_WITH_GUAM ? 'gap open' : 'gap closed'}
            className="h-4 w-4"
          />
          {blackout} min
        </div>
        {!guamActive ? (
          <TextButton onClick={startGuamDemo} className="flex items-center gap-1 bg-accent-soft text-accent">
            <Play className="h-3.5 w-3.5" /> Start
          </TextButton>
        ) : (
          <>
            <TextButton onClick={addGuamStation} disabled={hasGuam} className="flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> {hasGuam ? 'Guam added' : 'Add Guam station'}
            </TextButton>
            <TextButton onClick={stopGuamDemo} className="flex items-center gap-1">
              <Square className="h-3.5 w-3.5" /> Stop
            </TextButton>
          </>
        )}
      </div>

      {regions.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {regions.map((g) => (
            <button
              key={g.name}
              type="button"
              onClick={() => getGlobeEngine()?.flyTo(g.centerLon, g.centerLat, 12_000_000, 1.5)}
              className="flex flex-col gap-1 rounded-panel border border-hairline bg-inset/50 p-3 text-left hover:border-hairline-strong"
            >
              <div className="flex items-center gap-2">
                <MapPin aria-hidden className="h-3.5 w-3.5 text-ink-3" />
                <span className="font-medium text-ink">{g.name}</span>
                <Chip
                  tone={SEVERITY_TONE[(g.severity as keyof typeof SEVERITY_TONE) ?? 'low'] ?? 'neutral'}
                  className="ml-auto"
                >
                  {g.severity ?? 'gap'}
                </Chip>
              </div>
              {g.description && <div className="leading-snug text-ink-2">{g.description}</div>}
              <div className="font-mono text-ink-3">
                {g.centerLat.toFixed(1)}, {g.centerLon.toFixed(1)}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-ink-3">No gap regions defined for this network.</div>
      )}
    </div>
  );
}
